// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import TextField from 'presentations/views/components/atoms/TextField';
import TextArea from 'presentations/views/components/atoms/TextArea';
import Icon from 'presentations/views/components/atoms/Icon';
import ChipTextField from 'presentations/views/components/atoms/ChipTextField';
import { LocationType } from 'domains/models/event_type/locationType';
import {
  spacing,
  fontSize,
  zIndex,
  isMediaMax,
} from 'presentations/views/styles';
import { ErrorList } from 'domains/models';
import Workflow from 'domains/models/workflow';
import { WorkflowType } from 'domains/models/workflow/workflowType';
import Radio from 'presentations/views/components/atoms/Radio';
import Checkbox from 'presentations/views/components/atoms/Checkbox';
import FormLabel from 'presentations/views/components/molecules/FormLabel';
import { EventTypeServiceListItem } from 'presentations/api/schema/event_type_service';
import Select from 'presentations/views/components/atoms/Select';
import ErrorText from 'presentations/views/components/atoms/ErrorText';
import TextFieldPhoneInput from '../../atoms/TextFieldPhoneInput';
import { formatPhoneNumber } from 'presentations/views/helpers/formatter';
import { useState } from 'react';
import { primary } from 'presentations/views/styles/color';
import RichReadView from 'presentations/views/components/atoms/RichReadView';

export type Props = {
  onChange?: any;
  onChangeTextRemindNumber?: any;
  onInputChangeTag?: any;
  onDeleteTag?: any;
  onSubmitTag?: any;
  onSubmit?: any;
  onBlur?: any;
  isLoading?: boolean;
  email: string | null;
  name: string | null;
  company: string | null;
  ccEmails: string[];
  message: string | null;
  phoneNumber: string | null;
  textRemindCountry: any | null;
  textRemindNumber: string | null;
  locationId: LocationType | null;
  isDisabled: boolean;
  errors: ErrorList;
  customQuestions: Workflow[];
  customQuestionAnswers: any[];
  customOtherAnswers: string[];
  onClickOtherAnswer?: Function;
  isCompanyHidden?: boolean;
  isCompanyRequired?: boolean;
  isCcEmailsHidden?: boolean;
  isCcEmailsRequired?: boolean;
  isMessageHidden?: boolean;
  isMessageRequired?: boolean;
  isPhoneNumberRequired?: boolean;
  isTextRemindNumberRequired?: boolean;
  isTextRemind?: boolean;
  color: string;
  isLightColor: boolean;
  selectedServices: EventTypeServiceListItem[];
  isPaidServiceSelected: boolean;
  isProvidedContact: boolean;
  isRequest: boolean;
  isSelectedServicesHidePriceOnly: boolean;
};

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const isMobile = isMediaMax('tablet');
  const [isShowEmailsForm, setIsShowEmailsForm] = useState<boolean>(false);
  const onClickAddGuestEmails = () => {
    setIsShowEmailsForm(!isShowEmailsForm);
  };

  return (
    <>
      <Block>
        {props.isProvidedContact ? (
          <>
            <FormLabel>{t('Name')}</FormLabel>
            <IntegrationText>{props.name}</IntegrationText>
          </>
        ) : (
          <TextField
            isAutoFocus={!isMobile}
            isRequired={!props.isProvidedContact}
            isFullWidth
            label={t('Name')}
            name="name"
            value={props.name}
            placeholder={t('Name placeholder')}
            onChange={props.onChange}
            onBlur={props.onBlur}
            errors={props.errors['name']}
            isDisabled={props.isProvidedContact}
          />
        )}
      </Block>
      <Block>
        {props.isProvidedContact ? (
          <>
            <FormLabel>{t('Email')}</FormLabel>
            <IntegrationText>{props.email}</IntegrationText>
          </>
        ) : (
          <>
            <TextField
              isRequired={!props.isProvidedContact}
              isFullWidth
              label={t('Email')}
              name="email"
              value={props.email}
              placeholder={t('Email placeholder')}
              onChange={props.onChange}
              onBlur={props.onBlur}
              errors={props.errors['email']}
              isDisabled={props.isProvidedContact}
            />
            {!props.isCcEmailsHidden &&
              !props.isCcEmailsRequired &&
              props.ccEmails.length === 0 &&
              !isShowEmailsForm && (
                <AddGuestEmailsContainer onClick={onClickAddGuestEmails}>
                  <AddGuestEmailsIcon name="plus" />
                  {t('AddGuestEmails')}
                </AddGuestEmailsContainer>
              )}
          </>
        )}
      </Block>
      {!props.isCcEmailsHidden &&
        (props.isCcEmailsRequired ||
          props.ccEmails.length > 0 ||
          isShowEmailsForm) && (
          <Block>
            <ChipTextField
              isRequired={!!props.isCcEmailsRequired}
              isNotRequired={!props.isCcEmailsRequired}
              isFullWidth
              name={'ccEmails'}
              value={props.ccEmails}
              label={t('ccEmails')}
              onInputChange={props.onInputChangeTag}
              placeholder={t('ccEmails placeholder')}
              onDeleteTag={props.onDeleteTag}
              onSubmit={props.onSubmitTag}
              errors={props.errors['ccEmails']}
              description={t('AddGuestEmails text')}
            />
          </Block>
        )}
      {props.locationId === LocationType.Phone && (
        <Block>
          <TextField
            isRequired={props.isPhoneNumberRequired}
            isNotRequired={!props.isPhoneNumberRequired}
            isFullWidth
            label={t('Phone')}
            name="phoneNumber"
            value={props.phoneNumber}
            placeholder={t('Phone placeholder')}
            onChange={props.onChange}
            errors={props.errors['phoneNumber']}
          />
        </Block>
      )}
      {!props.isCompanyHidden && (
        <Block>
          <TextField
            isRequired={!!props.isCompanyRequired}
            isNotRequired={!props.isCompanyRequired}
            isFullWidth
            label={t('Company')}
            name="company"
            value={props.company}
            placeholder={t('Company placeholder')}
            onChange={props.onChange}
            onBlur={props.onBlur}
            errors={props.errors['company']}
          />
        </Block>
      )}
      {!!props.isTextRemind && (
        <Block style={{ zIndex: zIndex(100), position: 'relative' }}>
          <TextFieldPhoneInput
            country={props.textRemindCountry?.countryCode}
            value={formatPhoneNumber(
              props.textRemindCountry?.dialCode,
              props.textRemindNumber,
            )}
            isRequired={!!props.isTextRemindNumberRequired}
            isNotRequired={!props.isTextRemindNumberRequired}
            isFullWidth
            label={t('Phone reminder')}
            name="textRemindNumber"
            placeholder={t('Phone placeholder')}
            onChange={(value: string, country: any) => {
              props.onChangeTextRemindNumber(value, country);
            }}
            errors={props.errors['textRemindNumber']}
          />
        </Block>
      )}
      {props.customQuestions.map((question, index) => {
        switch (question.workflowType) {
          case WorkflowType.QuestionAnswerText:
            return (
              <Block>
                <TextField
                  isRequired={question.isRequired}
                  isNotRequired={!question.isRequired}
                  isFullWidth
                  label={question.content}
                  name={`customQuestion.${index}`}
                  value={props.customQuestionAnswers[index]}
                  placeholder={t('custom placeholder')}
                  onChange={props.onChange}
                  errors={props.errors[`customQuestion.${index}`]}
                />
              </Block>
            );
          case WorkflowType.QuestionAnswerMultilineText:
            return (
              <Block>
                <TextArea
                  isRequired={question.isRequired}
                  isNotRequired={!question.isRequired}
                  isFullWidth
                  label={question.content}
                  name={`customQuestion.${index}`}
                  value={props.customQuestionAnswers[index]}
                  placeholder={t('custom placeholder')}
                  onChange={props.onChange}
                  errors={props.errors[`customQuestion.${index}`]}
                />
              </Block>
            );
          case WorkflowType.QuestionAnswerCheckboxes:
            return (
              <Block>
                <FormLabel
                  isRequired={question.isRequired}
                  isNotRequired={!question.isRequired}
                >
                  {question.content}
                </FormLabel>
                <SelectList>
                  {question.choices &&
                    question.choices.map((choice) => (
                      <SelectListItem>
                        <Checkbox
                          name={`customQuestion.${index}`}
                          value={choice.value}
                          onChange={props.onChange}
                          isChecked={
                            typeof props.customQuestionAnswers[index] ===
                              'object' &&
                            props.customQuestionAnswers[index].includes(
                              choice.value,
                            )
                          }
                          label={choice.value}
                        />
                      </SelectListItem>
                    ))}
                  {question.hasOtherChoice && (
                    <SelectListItem>
                      <Checkbox
                        name={`customQuestion.${index}`}
                        value={t('other')}
                        onChange={props.onChange}
                        isChecked={
                          typeof props.customQuestionAnswers[index] ===
                            'object' &&
                          props.customQuestionAnswers[index].includes(
                            t('other'),
                          )
                        }
                        label={t('other')}
                      />
                    </SelectListItem>
                  )}
                </SelectList>
                {question.hasOtherChoice && (
                  <TextArea
                    isFullWidth
                    name={`customQuestion.${index}`}
                    placeholder={t('custom other choice placeholder')}
                    value={props.customOtherAnswers[index]}
                    onChange={props.onClickOtherAnswer}
                  />
                )}
                {props.errors[`customQuestion.${index}`] &&
                  props.errors[
                    `customQuestion.${index}`
                  ].map((errorText, i) => (
                    <ErrorText key={i}>{errorText}</ErrorText>
                  ))}
              </Block>
            );
          case WorkflowType.QuestionAnswerMultipleChoice:
            return (
              <Block>
                <FormLabel
                  isRequired={question.isRequired}
                  isNotRequired={!question.isRequired}
                >
                  {question.content}
                </FormLabel>
                <SelectList>
                  {question.choices &&
                    question.choices.map((choice) => (
                      <SelectListItem>
                        <Radio
                          name={`customQuestion.${index}`}
                          isChecked={
                            props.customQuestionAnswers[index] === choice.value
                          }
                          label={choice.value}
                          onChange={props.onChange}
                          value={choice.value}
                        />
                      </SelectListItem>
                    ))}
                  {question.hasOtherChoice && (
                    <>
                      <SelectListItem>
                        <Radio
                          name={`customQuestion.${index}`}
                          isChecked={
                            props.customQuestionAnswers[index] === t('other')
                          }
                          label={t('other')}
                          onChange={props.onChange}
                          value={t('other')}
                        />
                      </SelectListItem>
                    </>
                  )}
                </SelectList>
                {question.hasOtherChoice && (
                  <TextArea
                    isFullWidth
                    name={`customQuestion.${index}`}
                    value={props.customOtherAnswers[index]}
                    placeholder={t('custom other choice placeholder')}
                    onChange={props.onClickOtherAnswer}
                  />
                )}
                {props.errors[`customQuestion.${index}`] &&
                  props.errors[
                    `customQuestion.${index}`
                  ].map((errorText, i) => (
                    <ErrorText key={i}>{errorText}</ErrorText>
                  ))}
              </Block>
            );
          case WorkflowType.QuestionAnswerDropDown:
            const options: any = [{ text: t('select'), value: '' }];

            question.choices?.forEach((c) =>
              options.push({ text: c.value, value: c.value }),
            );
            if (question.hasOtherChoice) {
              options.push({ text: t('other'), value: t('other') });
            }
            return (
              <Block>
                <FormLabel
                  isRequired={question.isRequired}
                  isNotRequired={!question.isRequired}
                >
                  {question.content}
                </FormLabel>
                <SelectWrap
                  name={`customQuestion.${index}`}
                  value={props.customQuestionAnswers[index]}
                  onChange={props.onChange}
                  isFullWidth
                  options={options}
                />
                {question.hasOtherChoice && (
                  <>
                    {props.customQuestionAnswers[index] === t('other') && (
                      <TextArea
                        isFullWidth
                        name={`customQuestion.${index}`}
                        value={props.customOtherAnswers[index]}
                        placeholder={t('custom other choice placeholder')}
                        onChange={props.onClickOtherAnswer}
                      />
                    )}
                  </>
                )}
                {props.errors[`customQuestion.${index}`] &&
                  props.errors[
                    `customQuestion.${index}`
                  ].map((errorText, i) => (
                    <ErrorText key={i}>{errorText}</ErrorText>
                  ))}
              </Block>
            );
          case WorkflowType.QuestionAnswerHostInsertText:
            return (
              <Block>
                <RichReadView content={question.content} />
              </Block>
            );
          default:
            return null;
        }
      })}
      {!props.isMessageHidden && (
        <Block>
          <TextArea
            isRequired={!!props.isMessageRequired}
            isNotRequired={!props.isMessageRequired}
            isFullWidth={true}
            label={t('Message')}
            name="message"
            value={props.message}
            placeholder={t('Message placeholder')}
            onChange={props.onChange}
            errors={props.errors['message']}
          />
        </Block>
      )}
    </>
  );
}

const Block = styled.div`
  margin-bottom: ${spacing(40)};
  :last-of-type {
    /* margin-bottom: 0; */
  }
`;

const SelectList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const SelectListItem = styled.div`
  margin-right: ${spacing(20)};
`;

const SelectWrap = styled(Select)`
  width: 100%;
  margin-bottom: ${spacing(8)};
  .MuiSelect-select {
    font-size: ${fontSize(14)};
  }
`;

const IntegrationText = styled.div`
  font-size: ${fontSize(16)};
`;

const AddGuestEmailsContainer = styled.div`
  color: ${primary};
  cursor: pointer;
  margin-top: ${spacing(8)};
  display: flex;
  align-items: center;
`;

const AddGuestEmailsIcon = styled(Icon)`
  color: ${primary};
  width: 18px !important;
  height: 18px !important;
`;
